/** Parse "HH:mm" or "HH:mm:ss" into hour (0-23) and minute (0-59). */
function parseTimeParts(str) {
  if (str == null || str === '') return null;
  const s = String(str).trim();
  const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/** Normalize to "HH:mm" for API / form state. */
export function normalizeTime24(str) {
  const parts = parseTimeParts(str);
  if (!parts) return '';
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
}

/** Convert 24h time string to 12-hour display, e.g. "14:00" → "2:00 PM". */
export function formatTime12h(str) {
  const parts = parseTimeParts(str);
  if (!parts) return '';
  const { hour, minute } = parts;
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

/** Format a start–end range in 12-hour form. */
export function formatTimeRange12h(start, end) {
  const a = formatTime12h(start);
  const b = formatTime12h(end);
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  return `${a} – ${b}`;
}

/** Parse 24h string into 12-hour picker parts. */
export function parseTime24To12(str) {
  const parts = parseTimeParts(str);
  if (!parts) {
    return { hour: 12, minute: 0, period: 'AM' };
  }
  const { hour, minute } = parts;
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return { hour: hour12, minute, period };
}

/** Build 24h "HH:mm" from 12-hour picker values. */
export function time12To24(hour12, minute, period) {
  let h = Number(hour12);
  const m = Number(minute);
  if (!Number.isFinite(h) || h < 1 || h > 12) h = 12;
  if (!Number.isFinite(m) || m < 0 || m > 59) return '';
  const isPm = String(period).toUpperCase() === 'PM';
  let hour24;
  if (h === 12) {
    hour24 = isPm ? 12 : 0;
  } else {
    hour24 = isPm ? h + 12 : h;
  }
  return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Minutes since midnight from a time string. */
export function timeToMinutes(str) {
  const parts = parseTimeParts(str);
  if (!parts) return null;
  return parts.hour * 60 + parts.minute;
}

/** Compact hour label for calendar axis, e.g. 13 → "1 PM". */
export function formatHourLabel12h(hour24) {
  const h = Number(hour24);
  if (!Number.isFinite(h) || h < 0 || h > 23) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12} ${period}`;
}

const DEFAULT_GRID_START = 7;
const DEFAULT_GRID_END = 18;

/** Grid hour bounds from period entries with padding. */
export function getTimeGridBounds(entries = [], paddingHours = 1) {
  let minMin = null;
  let maxMin = null;
  for (const entry of entries) {
    const start = timeToMinutes(entry?.start_time);
    const end = timeToMinutes(entry?.end_time);
    if (start != null) minMin = minMin == null ? start : Math.min(minMin, start);
    if (end != null) maxMin = maxMin == null ? end : Math.max(maxMin, end);
  }
  if (minMin == null || maxMin == null) {
    return { startHour: DEFAULT_GRID_START, endHour: DEFAULT_GRID_END };
  }
  const pad = paddingHours * 60;
  const startHour = Math.max(0, Math.floor((minMin - pad) / 60));
  const endHour = Math.min(23, Math.ceil((maxMin + pad) / 60));
  return { startHour, endHour: Math.max(startHour + 1, endHour) };
}
